import React from 'react'

import { globalNavStyles, logoLinkStyles } from './styles'
import { MainNav } from './MainNav'
import { PrintHeader } from './PrintHeader'
import { Icon, IconSize } from 'sidecar-components/atoms/Icon'
import { Link, LinkType } from 'sidecar-components/atoms/Link'
import { SidecarLogo } from 'sidecar-components/atoms/Icon'
import { GlobalNavProps } from './GlobalNav.types'
import { Sentiment } from 'sidecar-components/types'

/**
 * The main global navigation header. Should be on the top for large screens and
 * on the bottom for small screens.
 *
 * @param {boolean} authenticated - Whether or not the user has logged in or
 *   not. Controls whether the SH logo should go to the dashboard or marketing
 *   site.
 * @param {ReactNode} children - Meant to hold the HelpNav.
 * @param {boolean} isNativeApp - Whether or not the user is in the native app.
 *   Controls whether the SH logo should go to the dashboard or marketing site.
 * @param {Array} mainNavItems - List of links the nav should go to.
 * @param {Sentiment.DEFAULT | Sentiment.PRIMARY} sentiment - Whether the
 *   background color should be 'primary' blue (log in or forget password pages)
 *   or 'default' white (all other pages)
 * @returns {JSX.Element} - An HTML <header> element
 */
export const GlobalNav = ({
  authenticated = false,
  children,
  isNativeApp = false,
  mainNavItems = [],
  sentiment = Sentiment.DEFAULT,
}: GlobalNavProps) => {
  const hasMainNavItems = mainNavItems.length > 0

  // If the user is logged in or logged out but in our native app (has ?webview=true in the URL),
  // clicking the logo should keep the user in the app.
  // Otherwise, it should go to the marketing site.
  const stayInApp = authenticated || isNativeApp

  return (
    <header className={globalNavStyles({ hasMainNavItems, sentiment })}>
      <Link
        className={logoLinkStyles(sentiment)}
        path={stayInApp ? '/' : 'https://sidecarhealth.com'}
        type={stayInApp ? LinkType.INTERNAL : LinkType.EXTERNAL}
        testId="info_logo"
        aria-label="Home"
      >
        {/* Text for screen readers to read */}
        <div className="sr-only">Sidecar Health</div>

        {/* Mobile logo only has the side car */}
        <Icon
          name="sidecar"
          // only show on tablet
          className="hidden md:max-lg:block"
          size={IconSize.XXL}
        />

        {/* Desktop logo has the side car and text */}
        <SidecarLogo className="shrink-0 hidden lg:block print:block" />
      </Link>

      {/* Only load the MainNav if there are items to fill it. 
        Otherwise, mobile will have a small box fixed to the bottom of the screen. */}
      {mainNavItems.length > 0 && (
        <MainNav navItems={mainNavItems} sentiment={sentiment} />
      )}

      {/* We pass in the helpnav as a child so that we don't have to instead
        pass all the necessary props for it as well. */}
      {children}

      {/* Print header is only visible when printing */}
      <PrintHeader authenticated={authenticated} />
    </header>
  )
}
